/**
 * 宿主供给模型（REQ-244）—— 「哪个宿主能提供 requires 四轴里的哪些项」的唯一数据源，
 * 加上把「供给 × 需求」折成缺席清单的唯一算子 {@link resolveGaps}。
 *
 * 此前同一件事散在三处各写一遍：WorkBuddy 投影的 `shouldProject` 硬编码「三轴全空」、
 * Codex 适配说明按 `requires.tools ∪ optional.tools` 现算、一键卡片的降级提示行按簇白名单
 * 现算差集。三处的判据形状不同、口径靠注释对齐——注释钉不住任何东西。本模块把「供给」
 * 抽成一等公民（宿主供给 = {@link hostSupply}，簇供给 = {@link clusterSupply}），三处共用
 * 同一个 {@link resolveGaps}。
 *
 * **供给不是能力清单**：这里只记「requires 里能写出来的名字，这个宿主给不给」。宿主自带
 * 但不在 `capability-requires-vocab.json` 域里的东西（Codex 的 shell、WorkBuddy 系统分配的
 * 原生工具、agent-host 上的 feishu_* / relay facade 工具）一律记 `none` 而不是「不缺」——
 * fail-closed：技能声明的那个具体名字够不着，就是缺席。
 */
import { CHANNEL_ORIGIN_DISALLOWED_APP_TOOL_LEAVES, CONVERSATION_TURN_DISALLOWED_APP_TOOL_LEAVES } from "./channel-origin-bans.js";
import { MOBILE_ALLOWED_APP_TOOL_LEAVES } from "./mobile-entry.js";
import { CAPABILITY_REQUIRES_VOCAB } from "./skill-requires.js";
import hostCapabilitiesJson from "./host-capabilities.json" with { type: "json" };
export const HOST_IDS = [
    "claude-desktop",
    "pi-desktop",
    "agent-host",
    "codex",
    "workbuddy",
    "feishu-bot",
];
/** 插件随附段（N-31）的三值：插件 cliTools 能否随提供方插件到达该宿主会话的 PATH。 */
export const PLUGIN_CLI_SUPPLY_VALUES = ["with-plugin", "none", "unverified"];
/** H 类（宿主独有）工具名单。与 `docs/design/capability-pluggability-2026-09-02/inprocess-tool-ownership.md` H 表同源，测试逐条对齐。 */
export const HOST_ONLY_TOOLS = readHostOnlyTools();
const AXIS_VOCAB = {
    tools: CAPABILITY_REQUIRES_VOCAB.tools,
    native: CAPABILITY_REQUIRES_VOCAB.native,
    // connectors 域 = live 插件 `.mcp.json` 键，不是随包固化的清单（见 skill-requires.ts
    // 头注释）。没有静态 vocab 可校验，故只校验元素是非空字符串。
    connectors: undefined,
};
function asRecord(value, where) {
    if (value === null || typeof value !== "object" || Array.isArray(value)) {
        throw new Error(`host-capabilities.json: ${where} must be a mapping`);
    }
    const out = {};
    for (const [key, entry] of Object.entries(value))
        out[key] = entry;
    return out;
}
/**
 * 一轴取值 → 集合。数组元素必须 ⊆ 该轴 vocab：表里把工具名拼错时，静默当成「这个宿主
 * 没有它」= 判据悄悄换了个意思；抛错才看得见。
 */
function readAxis(raw, host, axis) {
    const where = `hosts.${host}.${axis}`;
    if (raw === "all")
        return "all";
    if (raw === "none")
        return new Set();
    if (!Array.isArray(raw)) {
        throw new Error(`host-capabilities.json: ${where} must be an array, "all" or "none"`);
    }
    const vocab = AXIS_VOCAB[axis];
    const out = new Set();
    for (const item of raw) {
        if (typeof item !== "string" || !item.trim()) {
            throw new Error(`host-capabilities.json: ${where} entries must be non-empty strings`);
        }
        const name = item.trim();
        if (vocab && !vocab.includes(name)) {
            throw new Error(`host-capabilities.json: ${where} has unknown ${axis} name: ${name}`);
        }
        out.add(name);
    }
    return out;
}
function readSubagents(raw, host) {
    if (typeof raw === "boolean" || raw === "lead-only")
        return raw;
    throw new Error(`host-capabilities.json: hosts.${host}.subagents must be true, false or "lead-only"`);
}
function isPluginCliSupply(raw) {
    return typeof raw === "string" && PLUGIN_CLI_SUPPLY_VALUES.includes(raw);
}
/**
 * 一个宿主的 `bins` 登记 → {@link BinsSupply}（N-31 R3）。缺失、形状不对、宿主段出现名字域外的
 * 名字（插件 CLI 名也算域外——它归插件段）、`pluginCli` 不在三值内，一律抛错并带字段名：
 * 供给表是判据的输入，读坏了不能降级成「按空集算」。
 */
export function parseBinsSupply(raw, host) {
    const where = `hosts.${host}.bins`;
    const entry = asRecord(raw, where);
    let hostBins;
    if (entry.host === "unverified") {
        hostBins = "unverified";
    }
    else if (Array.isArray(entry.host)) {
        const names = new Set();
        for (const item of entry.host) {
            if (typeof item !== "string" || !item.trim()) {
                throw new Error(`host-capabilities.json: ${where}.host entries must be non-empty strings`);
            }
            const name = item.trim();
            if (!CAPABILITY_REQUIRES_VOCAB.hostBins.includes(name)) {
                throw new Error(`host-capabilities.json: ${where}.host has unknown hostBins name: ${name}`);
            }
            names.add(name);
        }
        hostBins = names;
    }
    else {
        throw new Error(`host-capabilities.json: ${where}.host must be an array of hostBins names or "unverified"`);
    }
    if (!isPluginCliSupply(entry.pluginCli)) {
        throw new Error(`host-capabilities.json: ${where}.pluginCli must be one of ${PLUGIN_CLI_SUPPLY_VALUES.join(" | ")}`);
    }
    return { host: hostBins, pluginCli: entry.pluginCli };
}
/** 宿主会话挂不挂某一层员工文件（N-182 P3）：挂 / 不挂 / 没核实。 */
export const FILE_LAYER_SUPPLY_VALUES = ["mounted", "none", "unverified"];
function isFileLayerSupply(raw) {
    return typeof raw === "string" && FILE_LAYER_SUPPLY_VALUES.includes(raw);
}
/**
 * 一个宿主的 `files` 登记 → {@link HostFileSupply}。缺失、形状不对、某层取值不在三值内一律抛错并带
 * 字段名：读坏了降级成「挂着」会让提示词漏说，降级成「不挂」会让它乱说。
 */
export function parseFileSupply(raw, host) {
    const where = `hosts.${host}.files`;
    const entry = asRecord(raw, where);
    const layer = (key) => {
        const value = entry[key];
        if (!isFileLayerSupply(value)) {
            throw new Error(`host-capabilities.json: ${where}.${key} must be one of ${FILE_LAYER_SUPPLY_VALUES.join(" | ")}`);
        }
        return value;
    };
    return { personal: layer("personal"), projects: layer("projects") };
}
const fileSupplyCache = new Map();
/** 读表得到某宿主的文件层登记（N-182 P3）。未登记的宿主、坏表都抛错。 */
export function hostFileSupply(host) {
    if (host === "mobile")
        return { ...CONVERSATION_FILE_SUPPLY };
    const cached = fileSupplyCache.get(host);
    if (cached)
        return cached;
    const hosts = asRecord(asRecord(hostCapabilitiesJson, "root").hosts, "hosts");
    if (!(host in hosts)) {
        throw new Error(`host-capabilities.json: no entry for host ${host}`);
    }
    const supply = parseFileSupply(asRecord(hosts[host], `hosts.${host}`).files, host);
    fileSupplyCache.set(host, supply);
    return supply;
}
const engineAbsentCache = new Map();
/**
 * 引擎没有、也没有同义工具顶替的原生工具（N-190，host-capabilities.json `engineNativeAbsent`）。
 * 宿主行的 `native` 说的是宿主给不给，这一份说的是任务写了这个引擎时要减掉什么。表里没写的引擎 = 什么都不缺。
 */
export function engineAbsentNative(engine) {
    const cached = engineAbsentCache.get(engine);
    if (cached)
        return cached;
    const table = asRecord(asRecord(hostCapabilitiesJson, "root").engineNativeAbsent, "engineNativeAbsent");
    const raw = table[engine] ?? [];
    if (!Array.isArray(raw))
        throw new Error(`host-capabilities.json: engineNativeAbsent.${engine} must be an array`);
    for (const name of raw) {
        if (typeof name !== "string" || !CAPABILITY_REQUIRES_VOCAB.native.includes(name)) {
            throw new Error(`host-capabilities.json: engineNativeAbsent.${engine} has ${String(name)}, which is not in the native vocab`);
        }
    }
    const absent = new Set(raw);
    engineAbsentCache.set(engine, absent);
    return absent;
}
function readHostOnlyTools() {
    const root = asRecord(hostCapabilitiesJson, "root");
    const raw = root.hostOnlyTools;
    if (!Array.isArray(raw)) {
        throw new Error("host-capabilities.json: hostOnlyTools must be an array");
    }
    const out = [];
    for (const item of raw) {
        if (typeof item !== "string" || !item.trim()) {
            throw new Error("host-capabilities.json: hostOnlyTools entries must be non-empty strings");
        }
        out.push(item.trim());
    }
    return out;
}
/**
 * 某宿主**自带**的连接器名单（REQ-246）。与 `connectors` 供给轴不是一回事：
 * `connectors` 说的是「技能 `requires.connectors` 里的名字这个宿主给不给」；
 * 这一份说的是「哪些 DOSIA 连接器插件在这个宿主那边已经内置，我们不必再发一份」。
 *
 * 它替掉 `scripts/gen-plugin-manifests.mjs` 里手写的 `CLI_SKIP` 表——那张表把
 * 「WorkBuddy 自带 feishu」这类**关于外部世界的判断**写死在脚本常量里，改一次要改代码，
 * 而且没人看得出它是根据什么定的。搬进供给表后，它和其余供给事实同源同格式。
 *
 * 未声明该字段 → 空数组（这个宿主没有内置连接器）。声明了就必须是非空字符串数组。
 */
export function hostBuiltinConnectors(host) {
    if (host === "mobile")
        return [];
    const hosts = asRecord(asRecord(hostCapabilitiesJson, "root").hosts, "hosts");
    if (!(host in hosts)) {
        throw new Error(`host-capabilities.json: no entry for host ${host}`);
    }
    const raw = asRecord(hosts[host], `hosts.${host}`).builtinConnectors;
    if (raw === undefined)
        return [];
    if (!Array.isArray(raw)) {
        throw new Error(`host-capabilities.json: hosts.${host}.builtinConnectors must be an array`);
    }
    const out = [];
    for (const item of raw) {
        if (typeof item !== "string" || !item.trim()) {
            throw new Error(`host-capabilities.json: hosts.${host}.builtinConnectors entries must be non-empty strings`);
        }
        out.push(item.trim());
    }
    return out;
}
const supplyCache = new Map();
/**
 * 读表得到某宿主的供给。表坏了抛错（带字段名）——供给表是判据的输入，读坏了整条判据
 * 就换了意思，不能降级成「按空表算」。
 */
export function hostSupply(host) {
    if (host === "mobile")
        return mobileConversationSupply();
    const cached = supplyCache.get(host);
    if (cached)
        return cached;
    const hosts = asRecord(asRecord(hostCapabilitiesJson, "root").hosts, "hosts");
    if (!(host in hosts)) {
        throw new Error(`host-capabilities.json: no entry for host ${host}`);
    }
    const entry = asRecord(hosts[host], `hosts.${host}`);
    const supply = {
        tools: readAxis(entry.tools, host, "tools"),
        native: readAxis(entry.native, host, "native"),
        connectors: readAxis(entry.connectors, host, "connectors"),
        subagents: readSubagents(entry.subagents, host),
        bins: parseBinsSupply(entry.bins, host),
    };
    supplyCache.set(host, supply);
    return supply;
}
/**
 * 宿主静态供给 + 运行期由插件 `.mcp.json` 声明的连接器名 → 本次判定用的供给。连接器域本来就是
 * 「live 插件 `.mcp.json` 键」（见上面 `AXIS_VOCAB` 注释），静态表登记不了某个组织此刻发布了哪些插件，
 * 所以静态行记宿主**自带**的部分（飞书机器人、云端 agent-host 都是空），判定时由调用方把本组织插件
 * 声明的连接器名并进来。两处调用方共用这一份并入：「能否发布到飞书」（`feishu-publish-readiness.ts`，
 * 名单来自企业后端已发布插件列表）与云端任务归属闸（N-30 / B-129，`apps/web` 的
 * `cloud-task-agent-locality.ts`，名单来自本组织 L5 里平台放行的插件）。
 *
 * 静态 `"all"` 保持 `"all"`；否则取并集。返回新对象——`hostSupply` 有缓存，就地并入会把这一次的组织
 * 插件名漏进之后每一次判定。
 */
export function withPluginConnectors(base, pluginConnectors) {
    if (base.connectors === "all")
        return base;
    return { ...base, connectors: new Set([...base.connectors, ...pluginConnectors]) };
}
/**
 * 飞书对话在统一运行时上的文件层（N-182 P2 第 24 条，PD-2）：会话目录与临时目录，不挂员工个人区、
 * 不挂项目。飞书读员工本人文件随 P3 记忆同批落，那时改这里。
 */
export const FEISHU_CONVERSATION_FILE_SUPPLY = { personal: "none", projects: "none" };
/** 手机对话的文件层与飞书对话相同（都是服务端对话轮次的文件面）。 */
const CONVERSATION_FILE_SUPPLY = FEISHU_CONVERSATION_FILE_SUPPLY;
/**
 * 飞书对话跑在 agent-host 上时实际拿得到的供给（N-182 P2 第 25 条）：agent-host 那行扣掉渠道来源禁令单与
 * 对话轮次另外不给的应用工具（`CONVERSATION_TURN_DISALLOWED_APP_TOOL_LEAVES`，两张单与 core 对话轮次的禁令单
 * 读同一份），文件层取 {@link FEISHU_CONVERSATION_FILE_SUPPLY}。
 *
 * 只有这一处算。它在删除飞书机器人本地执行路径的那份变更里接到 `hostSupply("feishu-bot")` 上；在那之前
 * 只供切换前的差集清单（第 26 条）用，`hostSupply("feishu-bot")` 仍读手填值——供给表是共享的静态数据，
 * 跟不了部署开关，切换期间宁可少放、不多放。
 */
export function deriveFeishuConversationSupply() {
    const base = hostSupply("agent-host");
    const banned = new Set([...CHANNEL_ORIGIN_DISALLOWED_APP_TOOL_LEAVES, ...CONVERSATION_TURN_DISALLOWED_APP_TOOL_LEAVES]);
    const tools = expandAxis(base.tools, "tools").filter((name) => !banned.has(name));
    return {
        supply: { ...base, tools: new Set(tools) },
        files: { ...FEISHU_CONVERSATION_FILE_SUPPLY },
    };
}
/**
 * 手机对话跑在 agent-host 上时实际拿得到的供给（N-182 P4 T12）：与飞书对话同一派生法，只是手机放开的应用工具不扣。中继的
 * 几件不在 requires 词表里，不进这条轴（判「做不到的事」时看的是本会话禁用工具）。只有这一处算。
 */
export function deriveMobileConversationSupply() {
    const base = hostSupply("agent-host");
    const banned = new Set([...CHANNEL_ORIGIN_DISALLOWED_APP_TOOL_LEAVES, ...CONVERSATION_TURN_DISALLOWED_APP_TOOL_LEAVES]);
    for (const leaf of MOBILE_ALLOWED_APP_TOOL_LEAVES)
        banned.delete(leaf);
    const tools = expandAxis(base.tools, "tools").filter((name) => !banned.has(name));
    return {
        supply: { ...base, tools: new Set(tools) },
        files: { ...CONVERSATION_FILE_SUPPLY },
    };
}
let mobileSupplyCache;
function mobileConversationSupply() {
    mobileSupplyCache ??= deriveMobileConversationSupply().supply;
    return mobileSupplyCache;
}
function expandAxis(value, axis) {
    return value === "all" ? [...CAPABILITY_REQUIRES_VOCAB[axis]] : [...value];
}
function axisDiff(before, after) {
    const b = new Set(before);
    const a = new Set(after);
    return {
        onlyInBefore: [...b].filter((name) => !a.has(name)).sort(),
        onlyInAfter: [...a].filter((name) => !b.has(name)).sort(),
    };
}
/** 比对两份供给，给切换清单逐项列出「之前有、之后没有」与「之前没有、之后有」。 */
export function diffCapabilitySupply(before, after) {
    const b = before.supply;
    const a = after.supply;
    const binsHost = (value) => value === "unverified" ? "unverified" : [...value].sort();
    const diff = {
        tools: axisDiff(expandAxis(b.tools, "tools"), expandAxis(a.tools, "tools")),
        native: axisDiff(expandAxis(b.native, "native"), expandAxis(a.native, "native")),
        connectors: b.connectors === "all" || a.connectors === "all"
            ? {
                before: b.connectors === "all" ? "all" : [...b.connectors].sort(),
                after: a.connectors === "all" ? "all" : [...a.connectors].sort(),
            }
            : axisDiff([...b.connectors], [...a.connectors]),
        files: ["personal", "projects"]
            .filter((layer) => before.files[layer] !== after.files[layer])
            .map((layer) => ({ layer, before: before.files[layer], after: after.files[layer] })),
    };
    if (b.subagents !== a.subagents)
        diff.subagents = { before: b.subagents, after: a.subagents };
    const bh = binsHost(b.bins.host);
    const ah = binsHost(a.bins.host);
    if (JSON.stringify(bh) !== JSON.stringify(ah))
        diff.binsHost = { before: bh, after: ah };
    if (b.bins.pluginCli !== a.bins.pluginCli)
        diff.pluginCli = { before: b.bins.pluginCli, after: a.bins.pluginCli };
    return diff;
}
/**
 * 簇供给（一键干活卡片）—— 与宿主供给同一个形状，喂同一个 {@link resolveGaps}。
 * 簇白名单是运行时装配出来的（`resolveClusterMcpServers` 会把已授权的可选连接器并进来），
 * 名字域也比 vocab 宽（`feishu_*` / `juguang_report` 等不在 requires vocab 里），所以这里
 * **不做 vocab 校验**：它不是随包固化的表，是这一轮真实装上的工具面。
 */
export function clusterSupply(dosiaTools, nativeTools, mcpServers) {
    return {
        tools: new Set(dosiaTools),
        native: new Set(nativeTools),
        connectors: new Set(mcpServers),
        subagents: false,
        // 簇供给只描述这一轮装上的工具面，不表达命令行工具；`resolveGaps` 也不看这一轴（N-31 R12）。
        bins: { host: "unverified", pluginCli: "unverified" },
    };
}
function missing(needed, supply) {
    if (supply === "all")
        return [];
    // 保序：输出直接进用户可见的提示行与投影日志，顺序跟着技能声明走，不跟着集合走。
    return needed.filter((name) => !supply.has(name));
}
/**
 * 需求 × 供给 → 缺席清单。三处判据（WorkBuddy 投不投 / Codex 注不注适配说明 /
 * 一键卡片提示行）共用它。
 *
 * `projectable` = 必需三轴零缺席 **且** `degradeConnectors` 为空。最后那半是 REQ-238 的
 * fail-closed：块标记只覆盖工具轴，一个可选连接器缺席时没有任何机制把正文换成降级写法，
 * 投过去就是一篇讲着够不着的连接器的技能。`bins` 不参与——WorkBuddy `cli.json` 另有分发腿。
 */
export function resolveGaps(requires, supply) {
    const missingTools = missing(requires.tools, supply.tools);
    const missingConnectors = missing(requires.connectors, supply.connectors);
    const missingNative = missing(requires.native, supply.native);
    const degradeTools = missing(requires.optional.tools, supply.tools);
    const degradeConnectors = missing(requires.optional.connectors, supply.connectors);
    return {
        projectable: missingTools.length === 0
            && missingConnectors.length === 0
            && missingNative.length === 0
            && degradeConnectors.length === 0,
        missingTools,
        missingConnectors,
        missingNative,
        degradeTools,
        degradeConnectors,
    };
}
//# sourceMappingURL=host-capabilities.js.map